import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { X, Printer, Download } from "lucide-react";

const DAY_LABELS = [
  ["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"],
  ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"],
];

function formatHours(hours) {
  if (!hours) return null;
  return DAY_LABELS.map(([key, label]) => {
    const h = hours[key];
    if (!h) return { label, value: "—" };
    if (h.closed) return { label, value: "Closed" };
    if (h.open && h.close) return { label, value: `${h.open}–${h.close}` };
    return { label, value: "—" };
  });
}

export default function PrintableQR({ open, onClose, businessName, username, note, hours, accent = "#FF7E67" }) {
  const cardRef = useRef(null);
  const [dataUrl, setDataUrl] = useState("");

  const url = `${window.location.origin}/find-a-baker?shop=${encodeURIComponent(username || "")}`;

  useEffect(() => {
    if (!open) return;
    document.body.classList.add("qr-modal-open");
    QRCode.toDataURL(url, {
      width: 480,
      margin: 1,
      color: { dark: "#3B2C24", light: "#FFFFFF" },
      errorCorrectionLevel: "M",
    })
      .then(setDataUrl)
      .catch(() => setDataUrl(""));
    return () => document.body.classList.remove("qr-modal-open");
  }, [open, url]);

  const printCard = () => {
    if (!dataUrl) return;
    const heading = "Scan to Find My<br/>Location and Hours of Operation";
    const subheading = "Favorite My Location on the Map to Receive Updates";
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>QR — ${businessName || username || "Cake Pop Talk"}</title>
    <style>
      * { box-sizing: border-box; }
      html, body { height: 100%; }
      body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; margin: 0; padding: 0.4in; text-align: center; color: #3B2C24; display: flex; flex-direction: column; justify-content: center; align-items: center; }
      h1 { font-family: Georgia, "Times New Roman", serif; font-size: 28px; font-weight: 700; line-height: 1.15; margin: 0 0 8px; }
      h2 { font-family: Georgia, "Times New Roman", serif; font-size: 18px; font-weight: 500; line-height: 1.35; margin: 0 0 18px; color: #5F4838; }
      .qr-wrap { display: inline-block; padding: 10px; border: 3px solid ${accent}; border-radius: 20px; }
      img { display: block; width: 260px; height: 260px; }
      .url { margin-top: 18px; font-size: 10px; color: #857163; letter-spacing: 0.5px; }
      @page { margin: 0.35in; size: auto; }
      @media print {
        body { padding: 0; page-break-inside: avoid; }
        .qr-wrap { page-break-inside: avoid; }
      }
    </style></head>
    <body>
      <div>
        <h1>${heading}</h1>
        <h2>${subheading}</h2>
        <div class="qr-wrap"><img src="${dataUrl}" alt="QR code"/></div>
        <div class="url">cakepoptalk.com/find-a-baker</div>
      </div>
      <script>
        window.addEventListener('load', function() {
          setTimeout(function() { window.print(); }, 200);
        });
        window.addEventListener('afterprint', function() { window.close(); });
      </script>
    </body></html>`;
    const w = window.open("", "_blank", "width=640,height=800");
    if (!w) { alert("Please allow pop-ups to print the QR card."); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `cake-pop-talk-qr-${username}.png`;
    a.click();
  };

  if (!open) return null;

  const rows = formatHours(hours);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 qr-modal" onClick={onClose} data-testid="qr-modal">
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[#EBDCD1] flex items-start justify-between no-print">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#857163]">QR code</div>
            <h2 className="font-heading text-xl text-[#3B2C24] mt-1">Print & share your locator</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F9EFE6] rounded-full" data-testid="qr-close"><X className="w-4 h-4" /></button>
        </div>

        {/* Printable card */}
        <div id="printable-qr-card" ref={cardRef} className="p-6 sm:p-8 mx-auto text-center" style={{ maxWidth: 480 }}>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#3B2C24] leading-tight">
            Scan to Find My<br/>Location and Hours of Operation
          </h1>
          <h2 className="font-heading text-base sm:text-lg text-[#5F4838] mt-3 leading-snug max-w-md mx-auto">
            Favorite My Location on the Map to Receive Updates
          </h2>
          <div className="mt-5 inline-block p-3 rounded-3xl border-2" style={{ borderColor: accent }}>
            {dataUrl ? (
              <img src={dataUrl} alt="QR code" className="w-56 h-56 sm:w-64 sm:h-64 block" data-testid="qr-image" />
            ) : (
              <div className="w-56 h-56 sm:w-64 sm:h-64 grid place-items-center text-[#857163] text-sm">Generating…</div>
            )}
          </div>
          <div className="mt-5 text-[10px] text-[#857163] break-all">cakepoptalk.com/find-a-baker</div>
        </div>

        <div className="p-4 border-t border-[#EBDCD1] flex justify-end gap-2 no-print">
          <button onClick={download} disabled={!dataUrl} className="cp-btn-ghost inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="qr-download">
            <Download className="w-4 h-4" /> Download PNG
          </button>
          <button onClick={printCard} disabled={!dataUrl} className="cp-btn-primary inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="qr-print">
            <Printer className="w-4 h-4" /> Print
          </button>
        </div>
      </div>
    </div>
  );
}
